import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AgeGateModal from "@/components/AgeGateModal";
import ProtectedRoute from "@/components/ProtectedRoute";
import PortalLayout from "@/components/PortalLayout";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Partners from "@/pages/Partners";
import About from "@/pages/About";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import DosageCalculator from "@/pages/DosageCalculator";

import CustomerDashboard from "@/pages/customer/Dashboard";
import CustomerOrders from "@/pages/customer/Orders";
import CustomerOrderDetail from "@/pages/customer/OrderDetail";
import CustomerAddresses from "@/pages/customer/Addresses";
import CustomerDocuments from "@/pages/customer/Documents";
import CustomerProfile from "@/pages/customer/Profile";

import AffiliateDashboard from "@/pages/affiliate/Dashboard";
import AffiliateResources from "@/pages/affiliate/Resources";
import AffiliateProfile from "@/pages/customer/Profile"; // same profile view

import AdminApp from "@/pages/admin/AdminApp";
import AuthCallback from "@/pages/AuthCallback";

import "@/App.css";

function ReferralRedirect() {
  const { slug } = useParams();
  useEffect(() => {
    api.get(`/r/${slug}`).catch(() => {}).finally(() => { window.location.replace("/"); });
  }, [slug]);
  return <div className="mx-auto max-w-[1400px] px-6 py-24 overline animate-pulse">Applying referral…</div>;
}

const customerLinks = [
  { to: "/account", end: true, label: "Dashboard" },
  { to: "/account/orders", label: "Orders" },
  { to: "/account/addresses", label: "Addresses" },
  { to: "/account/documents", label: "Documents" },
  { to: "/account/profile", label: "Profile" },
];
const affiliateLinks = [
  { to: "/affiliate", end: true, label: "Dashboard" },
  { to: "/affiliate/resources", label: "Marketing resources" },
  { to: "/affiliate/profile", label: "Profile" },
];
const adminLinks = [
  { to: "/admin", end: true, label: "Dashboard" },
  { to: "/admin/products", label: "Products" },
  { to: "/admin/inventory", label: "Inventory" },
  { to: "/admin/orders", label: "Orders" },
  { to: "/admin/customers", label: "Customers" },
  { to: "/admin/affiliates", label: "Affiliates" },
  { to: "/admin/applications", label: "Applications" },
  { to: "/admin/commissions", label: "Commissions" },
  { to: "/admin/discounts", label: "Discounts" },
  { to: "/admin/coas", label: "COAs" },
  { to: "/admin/batches", label: "Batches" },
  { to: "/admin/audit-log", label: "Audit log" },
  { to: "/admin/settings", label: "Integration settings" },
];

function AppShell() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<Product />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/dosage-calculator" element={<DosageCalculator />} />
          <Route path="/r/:slug" element={<ReferralRedirect />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route
            path="/account"
            element={
              <ProtectedRoute roles={["customer", "affiliate", "admin"]}>
                <PortalLayout
                  title={{ overline: "Customer Portal", heading: "Your account" }}
                  links={customerLinks}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={<CustomerDashboard />} />
            <Route path="orders" element={<CustomerOrders />} />
            <Route path="orders/:id" element={<CustomerOrderDetail />} />
            <Route path="addresses" element={<CustomerAddresses />} />
            <Route path="documents" element={<CustomerDocuments />} />
            <Route path="profile" element={<CustomerProfile />} />
          </Route>

          <Route
            path="/affiliate"
            element={
              <ProtectedRoute roles={["affiliate", "admin"]}>
                <PortalLayout
                  title={{ overline: "PRMU Partners", heading: "Partner portal" }}
                  links={affiliateLinks}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={<AffiliateDashboard />} />
            <Route path="resources" element={<AffiliateResources />} />
            <Route path="profile" element={<AffiliateProfile />} />
          </Route>

          <Route
            path="/admin/*"
            element={
              <ProtectedRoute roles={["admin"]}>
                <PortalLayout
                  title={{ overline: "Admin", heading: "PRMU Control" }}
                  links={adminLinks}
                />
              </ProtectedRoute>
            }
          >
            <Route path="*" element={<AdminApp />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppShell />
          <Toaster position="top-right" richColors closeButton />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
